import React, { useState, useEffect } from 'react';
import { Shield, Eye, EyeOff, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ssoApi } from '../../services/api';

interface SSOProvider {
  id: string;
  name: string;
  type: 'oidc' | 'saml';
  icon_url?: string;
  enabled: boolean;
}

interface LoginPageProps {
  onLoginSuccess?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
  const { login, loading, error, isAuthenticated } = useAuth();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError]   = useState<string | null>(null);

  const [pwFocused, setPwFocused]          = useState(false);

  const [ssoProviders, setSsoProviders]   = useState<SSOProvider[]>([]);
  const [ssoLoading, setSsoLoading]       = useState(false);
  const [ssoInitiating, setSsoInitiating] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && onLoginSuccess) onLoginSuccess();
  }, [isAuthenticated, onLoginSuccess]);

  useEffect(() => {
    setSsoLoading(true);
    ssoApi.listProviders()
      .then((providers: any[]) => {
        setSsoProviders(
          (providers || [])
            .filter((p: any) => p.enabled !== false)
            .map((p: any) => ({
              id:       p.id ?? p.provider_id,
              name:     p.name ?? p.display_name ?? p.type?.toUpperCase(),
              type:     p.type ?? 'oidc',
              icon_url: p.icon_url,
              enabled:  p.enabled ?? true,
            }))
        );
      })
      .catch(() => { /* SSO service offline — form-only fallback */ })
      .finally(() => setSsoLoading(false));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) { setFormError('Email and password are required.'); return; }
    setFormError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      if (onLoginSuccess) onLoginSuccess();
    } catch (err: any) {
      setFormError(err?.message ?? 'Login failed. Check your credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSSOLogin = async (provider: SSOProvider) => {
    setSsoInitiating(provider.id);
    try {
      const { redirect_url } = await ssoApi.oidcAuthorize(provider.id);
      window.location.href = redirect_url;
    } catch {
      setFormError(`Failed to initiate ${provider.name} login. Please try again.`);
      setSsoInitiating(null);
    }
  };

  // Password strength calculation
  const pwStrength = (() => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 1) return { level: 0, label: 'Weak', color: '#ef4444' };
    if (score <= 2) return { level: 1, label: 'Fair', color: '#f97316' };
    if (score <= 3) return { level: 2, label: 'Good', color: '#eab308' };
    return { level: 3, label: 'Strong', color: '#22c55e' };
  })();

  const displayError = formError ?? error;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d0e14] via-[#131525] to-[#0d1117] flex items-center justify-center px-4">
      <div className="w-full max-w-md">

        {/* Logo + heading */}
        <div className="flex flex-col items-center mb-8 hero-glow">
          <div className="w-12 h-12 rounded-xl bg-[#325FE8]/10 border border-[#325FE8]/20 flex items-center justify-center mb-4 shadow-lg shadow-[#325FE8]/20">
            <Shield className="w-6 h-6 text-[#325FE8]" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">CyberShield</h1>
          <p className="text-gray-400 text-sm mt-1">Cloud Security Platform</p>
        </div>

        <div className="bg-[#1a1c2e] border border-gray-700/50 rounded-2xl overflow-hidden shadow-2xl card-elevated">

          {/* SSO providers */}
          {(ssoLoading || ssoProviders.length > 0) && (
            <div className="px-8 pt-8 pb-6 border-b border-gray-700/50">
              <p className="text-xs text-gray-400 uppercase tracking-widest mb-4 text-center">
                Continue with SSO
              </p>
              {ssoLoading ? (
                <div className="flex justify-center py-2">
                  <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                </div>
              ) : (
                <div className="space-y-2">
                  {ssoProviders.map(provider => (
                    <button
                      key={provider.id}
                      onClick={() => handleSSOLogin(provider)}
                      disabled={!!ssoInitiating}
                      className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg
                        border border-gray-600/50 bg-[#232538] hover:bg-theme-elevated hover:border-[#325FE8]/40
                        text-gray-200 text-sm font-medium transition-all duration-200 provider-card
                        disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {ssoInitiating === provider.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <ExternalLink className="w-4 h-4 text-gray-400" />
                      }
                      {provider.type === 'saml' ? 'SAML' : 'OIDC'}: {provider.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Credential form */}
          <form onSubmit={handleSubmit} className="px-8 py-8 space-y-5">
            <p className="text-xs text-gray-400 uppercase tracking-widest text-center -mt-2 mb-2">
              {ssoProviders.length > 0 ? 'Or sign in with credentials' : 'Sign in to your account'}
            </p>

            {displayError && (
              <div className="flex items-start gap-2.5 px-3.5 py-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{displayError}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <label className="text-xs text-gray-400 font-medium" htmlFor="login-email">
                Email address
              </label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@example.com"
                className="w-full px-3.5 py-2.5 rounded-lg bg-wiz-darker/80 border border-gray-600/50
                  text-gray-100 text-sm placeholder-gray-500
                  focus:outline-none focus:border-[#325FE8] focus:ring-2 focus:ring-[#325FE8]/30 focus:shadow-lg focus:shadow-[#325FE8]/10
                  transition-all duration-200"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-gray-400 font-medium" htmlFor="login-password">
                Password
              </label>
              <div className="relative">
                <input
                  id="login-password"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onFocus={() => setPwFocused(true)}
                  onBlur={() => setPwFocused(false)}
                  placeholder="••••••••"
                  className="w-full px-3.5 py-2.5 pr-10 rounded-lg bg-wiz-darker/80 border border-gray-600/50
                    text-gray-100 text-sm placeholder-gray-500
                    focus:outline-none focus:border-[#325FE8] focus:ring-2 focus:ring-[#325FE8]/30 focus:shadow-lg focus:shadow-[#325FE8]/10
                    transition-all duration-200"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200 transition-colors"
                  tabIndex={-1}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  aria-pressed={showPw}
                >
                  {showPw ? <EyeOff className="w-4 h-4" aria-hidden="true" /> : <Eye className="w-4 h-4" aria-hidden="true" />}
                </button>
              </div>
              {pwFocused && password.length > 0 && (
                <div className="mt-2">
                  <div className="flex gap-1">
                    {[0, 1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="h-1 flex-1 rounded-full transition-colors"
                        style={{ backgroundColor: i <= pwStrength.level ? pwStrength.color : '#e5e7eb' }}
                      />
                    ))}
                  </div>
                  <p className="text-xs mt-1" style={{ color: pwStrength.color }}>
                    {pwStrength.label}
                  </p>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting || loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                bg-[#325FE8] hover:bg-[#2850d0] text-white text-sm font-semibold
                transition-all duration-200 active:scale-[0.98] btn-press
                disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(submitting || loading) && <Loader2 className="w-4 h-4 animate-spin" />}
              Sign in
            </button>

            <p className="text-center text-xs text-gray-500 pt-1">
              Use your account credentials, or single sign-on above.
            </p>
          </form>
        </div>

        <p className="text-center text-gray-400 text-xs mt-6">
          &copy; {new Date().getFullYear()} CyberShield &middot; Cloud Security Platform
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
